import { useCallback, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Edit } from 'lucide-react'

import { UserDetail } from '@/components/admin/user-detail'
import { adminUserDisplayName } from '@/lib/admin-user-display'
import { useOpenUserEditCanvas } from '@/components/admin/use-open-user-edit-canvas'
import { useArtifactCanvas } from '@/components/artifact/artifact-canvas-context'
import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import {
  PageHeaderActionPrimary,
  PageHeaderActionSecondary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { useUser } from '@/hooks/use-admin-users'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'

import { adminPath, type AdminBasePath } from '@/components/admin/use-admin-base-path'

interface AdminUserDetailPageProps {
  basePath?: AdminBasePath
}

export function AdminUserDetailPage({ basePath = '/platform' }: AdminUserDetailPageProps) {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { closeCanvas } = useArtifactCanvas()
  const openUserEditCanvas = useOpenUserEditCanvas()
  const { data: user, isLoading, error } = useUser(userId ?? '')

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const usersListPath = adminPath(basePath, 'users')

  const handleEdit = useCallback(() => {
    if (!userId) return
    openUserEditCanvas(userId, user ? adminUserDisplayName(user) : undefined, {
      platformMode: basePath === '/platform',
    })
  }, [basePath, openUserEditCanvas, user, userId])

  if (!userId) {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading back={{ label: 'Back to users', to: usersListPath }} />} />
        <p className="text-sm text-muted-foreground">User not specified.</p>
      </PageContainer>
    )
  }

  if (isLoading) {
    return <EntityDetailSkeleton variant="profile" ariaLabel="Loading user" />
  }

  if (error || !user) {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading back={{ label: 'Back to users', to: usersListPath }} />} />
        <div className="text-center py-12">
          <p className="text-destructive">{(error as Error | undefined)?.message ?? 'User not found'}</p>
          <div className="mt-4">
            <PageHeaderActionSecondary
              type="button"
              onClick={() => navigateWithThreadPreservation(navigate, usersListPath, searchParams)}
            >
              Back to users
            </PageHeaderActionSecondary>
          </div>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading back={{ label: 'Back to users', to: usersListPath }} />}
        actions={
          <PageHeaderActions
            primary={
              <PageHeaderActionPrimary onClick={handleEdit}>
                <Edit className="h-4 w-4" aria-hidden />
                Edit user
              </PageHeaderActionPrimary>
            }
          />
        }
      />
      <UserDetail userId={userId} variant="page" onEdit={handleEdit} />
    </PageContainer>
  )
}
